import { AlertTriangle, ChevronDown, Pencil } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

export interface LocationMachineGroupProps {
  organisationName: string
  locationName: string
  machineCount: number
  hasWarning?: boolean
  defaultOpen?: boolean
  onEditClick?: () => void
  children: React.ReactNode
}

export function LocationMachineGroup({
  organisationName,
  locationName,
  machineCount,
  hasWarning = false,
  defaultOpen = true,
  onEditClick,
  children,
}: LocationMachineGroupProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-muted/50 rounded-lg">
        <div className="flex items-center gap-2 p-5">
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="flex h-auto flex-1 items-center justify-between p-0">
              <div className="text-left">
                <span className="text-muted-foreground text-sm font-normal">
                  {organisationName}
                </span>
                <h3 className="flex items-center gap-2 text-lg font-bold tracking-tight">
                  {locationName}{' '}
                  <span className="text-muted-foreground text-sm font-normal">
                    ({machineCount})
                  </span>
                  {hasWarning && <AlertTriangle className="text-destructive size-5 shrink-0" />}
                </h3>
              </div>
              <ChevronDown
                className={cn(
                  'text-muted-foreground size-5 transition-transform',
                  isOpen && 'rotate-180',
                )}
              />
            </Button>
          </CollapsibleTrigger>
          {onEditClick !== undefined && (
            <button
              type="button"
              onClick={onEditClick}
              className="text-muted-foreground hover:text-foreground rounded p-0.5 transition-colors"
              aria-label={`Edit ${locationName}`}
            >
              <Pencil className="size-4" />
            </button>
          )}
        </div>
        <CollapsibleContent>
          {machineCount > 0 ? (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-5 px-5 pb-5">
              {children}
            </div>
          ) : (
            <p className="text-muted-foreground p-5 text-center text-sm">
              No machines at this location.
            </p>
          )}
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}
